import { useEffect, useMemo } from 'react';
import { useCatalogItems } from './catalogItems';
import { useMeshDims } from './meshDims';
import { dropModelSize, setModelSize } from './partModelSize';
import { useCatalogModels } from './partModels';
import { partDbSize, usePartIdentities } from './partIdentity';
import { usePartsRun } from './partsRun';

// WHAT EVERY PART'S 3D MODEL MEASURES, kept in step with the model itself — mounted with the open
// manual (NoticeViewer), so no phase, subphase or viewer has to be open for it.
//
// THE MODEL IS THE PART'S MODEL IN THE PARTS DB, and nothing else: its box, off the server (the
// extents route), in millimetres by
// mmScale — the drawing's figure first, the Parts DB row's where this manual printed none
// (partDbSize). Re-measured whenever that model changes: a new one kept, a drill, a rescale moves
// its version (meshStamp), the Parts DB list is read again (invalidatePartModels), and the record
// follows. A part with no model there has no record.
export function ModelSizeSync({ docId }: { docId: string }) {
  const { items } = usePartsRun(docId);
  const identities = usePartIdentities(items);
  const { items: catalog } = useCatalogItems();
  const { models, loaded } = useCatalogModels();
  const measured = useMeshDims(useMemo(
    () => items.flatMap((p, i) => {
      const itemId = identities[i]?.itemId;
      return itemId == null ? [] : [{ itemId, dims: partDbSize(catalog, itemId, p.size) }];
    }),
    [items, identities, catalog],
  ));
  useEffect(() => {
    items.forEach((_, i) => {
      const itemId = identities[i]?.itemId;
      const got = itemId == null ? null : measured.get(itemId);
      if (got) setModelSize(docId, i, got);
      // Only once the Parts DB has answered: before that, "no model" is not known.
      else if (loaded && (itemId == null || !models.some((m) => m.itemId === itemId))) dropModelSize(docId, i);
    });
  }, [docId, items, identities, measured, models, loaded]);
  return null;
}
